import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import {
    Modal,
    Button,
    Select,
    TextInput,
    NumberInput,
    Grid,
    Stack,
    Group,
    Text,
    Divider,
    Paper,
    ScrollArea,
    SegmentedControl,
    Alert,
    Box,
    Card,
    SimpleGrid,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconLock } from '@tabler/icons-react';
import { collection, getDocs, query, Timestamp, where, addDoc, updateDoc, doc, deleteField } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useFirestore, useFunctions } from '../../helpers/firebaseContext';
import { Bond, CustodianAccount, CustomClaims, ManualTrade, MoneyMarketInstrument, Portfolio } from '../../types';
import BigNumber from 'bignumber.js';
import { ConfigContext, GlobalDataContext, MyUserDataContext } from '../../contexts';
import { GenevaApiSelect } from '../../components/GenevaApiSelect';
import { fromMantineDate } from '../../helpers/mantineDates';
import { getAllowedPortfolioTypeCodes, getEffectivePortfolioType } from './trades_permissions';

// Adjusted type for form state to handle Timestamp directly for maturityDate
type MoneyMarketInstrumentInForm = Omit<MoneyMarketInstrument, 'maturityDate'> & {
    maturityDate: Timestamp | null;
};

interface Props {
    opened: boolean;
    onClose: () => void;
    onSubmit?: (data: ManualTrade, isEdit: boolean) => void;
    tradeDate: Date;
    existingTrade?: ManualTrade & { id?: string }; // Optional trade for editing
}

// --- CONSTANTS ---
// Portfolio type code for the KFHC funds. For these portfolios, whether the
// Sukuk / Money Market instrument is tradable is driven by the fund's asset_class
// (from the funds collection) rather than hard-coded portfolio ids.
const FUND_PORTFOLIO_TYPE_CODE = 'KFHC Fund';
const ASSET_CLASS_SUKUK = 'Sukuk';
const ASSET_CLASS_MONEY_MARKET = 'Money Market';

const INITIAL_MONEY_MARKET: MoneyMarketInstrumentInForm = {
    id: 0,
    issuer: '',
    issuerCode: '',
    days: 0,
    maturityDate: null,
    exchange: 'OTC',
    description: '',
    rate: 0, // Added rate field
    issuerCountry: '',
    currency: '',
    // Money Market is always annual at maturity, so Coupon Frequency is hard-coded
    frequency: 'Annual',
    issueDate: null,
    firstCouponDate: null,
};

const INITIAL_NEW_SUKUK: Partial<Bond> = {
    ISIN: '',
    DESCRIPTION: '',
    COUPON: 0,
    MATURITYDATE: Timestamp.fromDate(new Date()),
    _INVESTMENTTYPE_CODE_: 'SK',
};

// Response of the functions-ai `sukukFromLseg` callable (dates are ISO strings).
type LsegSukukResult = {
    sukuk: Omit<Partial<Bond>, 'ISSUEDATE' | 'MATURITYDATE' | 'FIRSTCOUPONDATE'> & {
        ISSUEDATE: string | null;
        MATURITYDATE: string | null;
        FIRSTCOUPONDATE: string | null;
    };
    lsegIssuer: string;
    lsegSubIndustry: string;
    issuerConfidence: number | null;
    subIndustryConfidence: number | null;
    warning?: string;
};

// LSEG ISO date (UTC midnight) -> local-midnight Timestamp, same as the date pickers produce.
const lsegDateToTs = (iso: string | null) => {
    const date = iso ? fromMantineDate(iso.slice(0, 10)) : null;
    return date ? Timestamp.fromDate(date) : undefined;
};

const FREQUENCY_OPTIONS = [
    "Annual",
    "Bi-Monthly",
    "Daily",
    "Every 28 Days",
    "Every 35 Days",
    "Every 49 Days",
    "Every 7 Days",
    "Every 91 Days",
    "Every 182 Days",
    "First Wednesday",
    "Monthly",
    "Quarterly",
    "Semi Annual"
];

const createInitialFormData = (tradeDate: Date): ManualTrade => ({
    type: 'sukuk',
    portfolioId: '',
    transactionType: 'BUY',
    tradeDate: Timestamp.fromDate(tradeDate),
    settlementDate: Timestamp.fromDate(tradeDate),//Timestamp.fromDate(new Date(tradeDate.getTime() + 2 * 24 * 60 * 60 * 1000)), // T+2
    nominalValue: 0,
    pricePerUnit: 100,
    custodianAccountId: '',
    totalPrice: 0,
    kfhcFees: 0,
    principal: 0,
    accruedProfit: 0,
    settlementAmount: 0,
    yield: 0,
    counterpartyName: 'KFH',
    counterpartyAccount: '10255',
    moneyMarket: { ...INITIAL_MONEY_MARKET },
    state: 'PENDING',
});

// --- HELPER FUNCTIONS ---

// Mantine NumberInput emits strings for intermediate input ("100.", "").
const toNum = (v: string | number): number =>
    typeof v === 'number' ? v : (parseFloat(v) || 0);

// 30E/360 (European) day count — all months are treated as 30 days and the
// year as 360 days. Used for Sukuk accrued profit per the migration-script
// convention (AccrualDaysPerYear=360, AccrualDaysPerMonth=30E).
const days30E360 = (start: Date, end: Date): number => {
    let d1 = start.getDate();
    let d2 = end.getDate();
    const m1 = start.getMonth();
    const m2 = end.getMonth();
    const y1 = start.getFullYear();
    const y2 = end.getFullYear();
    if (d1 === 31) d1 = 30;
    if (d2 === 31) d2 = 30;
    return (y2 - y1) * 360 + (m2 - m1) * 30 + (d2 - d1);
};

// Returns the explicit period in months for recognized frequency strings, or
// null if the string is missing/unrecognized so callers can fall back to
// inferring the period from the dated-date → first-coupon-date gap.
const couponFrequencyMonthsExplicit = (freq?: string): number | null => {
    const f = (freq || '').toLowerCase().replace(/[\s_-]+/g, ' ').trim();
    if (!f) return null;
    switch (f) {
        case 'monthly':
        case 'every month': return 1;
        case 'bi monthly':
        case 'bimonthly': return 2;
        case 'quarterly': return 3;
        case 'semi annual':
        case 'semiannual': return 6;
        case 'annual':
        case 'yearly': return 12;
        default: return null;
    }
};

// Find the latest coupon payment date on or before the settlement date by
// stepping forward from the first coupon date by the coupon period. If the
// settlement is before the first coupon date, the dated date is used.
const findLastCouponDate = (
    firstCoupon: Date,
    settle: Date,
    periodMonths: number,
    datedDate: Date
): Date => {
    if (settle.getTime() < firstCoupon.getTime()) return datedDate;
    const cursor = new Date(firstCoupon);
    // Guard against pathological inputs (0 or negative period) that could loop forever.
    const step = Math.max(1, periodMonths);
    while (true) {
        const next = new Date(cursor);
        next.setMonth(next.getMonth() + step);
        if (next.getTime() > settle.getTime()) break;
        cursor.setTime(next.getTime());
    }
    return cursor;
};

// Sukuk always uses 30/360 basis: accrued = nominal * (coupon/100) * days30E360 / 360.
// Returns both the computed value and a `canCompute` flag. The caller can use
// `canCompute=false` to leave the field empty for the user to fill in manually.
const computeSukukAccruedProfit = (
    nominalValue: number,
    sukuk: Bond | undefined,
    settleDate: Timestamp | null | undefined,
    tradeDate: Timestamp | null | undefined
): { value: BigNumber; canCompute: boolean } => {
    const ZERO = { value: new BigNumber(0), canCompute: false };
    if (!sukuk || !sukuk.COUPON) return ZERO;
    const settleTs = settleDate ?? tradeDate;
    if (!settleTs) return ZERO;

    // Accrual starts at the dated date (falling back to issue date if not set).
    const accrualStartTs = sukuk.DATEDDATE ?? sukuk.ISSUEDATE;
    if (!accrualStartTs) return ZERO;

    const datedDate = accrualStartTs.toDate();
    const firstCouponTs = sukuk.FIRSTCOUPONDATE;
    const firstCoupon = firstCouponTs?.toDate() ?? datedDate;

    // The period is only reliably known when an explicit frequency is
    // provided. We also accept inference from the dated-date → first-coupon
    // gap, but only if a first coupon date was actually supplied.
    const explicit = couponFrequencyMonthsExplicit(sukuk.COUPON_FREQUENCY);
    let periodMonths: number | null = explicit;
    if (periodMonths === null && firstCouponTs) {
        const gap = (firstCoupon.getFullYear() - datedDate.getFullYear()) * 12
            + (firstCoupon.getMonth() - datedDate.getMonth());
        if (gap >= 1 && gap <= 12) periodMonths = gap;
    }
    if (periodMonths === null) return ZERO;

    const settle = settleTs.toDate();
    const lastCoupon = findLastCouponDate(firstCoupon, settle, periodMonths, datedDate);
    const days = days30E360(lastCoupon, settle);
    if (days <= 0) return { value: new BigNumber(0), canCompute: true };

    const value = new BigNumber(nominalValue)
        .multipliedBy(new BigNumber(sukuk.COUPON))
        .dividedBy(100)
        .multipliedBy(days)
        .dividedBy(360);
    return { value, canCompute: true };
};

const calculateSukukValues = (
    nominalValue: number,
    pricePerUnit: number,
    sukuk: Bond | undefined,
    isFeeless: boolean | undefined,
    custodianAccountId: string | undefined,
    settlementDate: Timestamp | null | undefined,
    tradeDate: Timestamp | null | undefined,
    currentAccruedProfit: number,
    preserveManualAccruedProfit: boolean
): Partial<ManualTrade> => {
    // Use BigNumber for precise decimal calculations
    const nominalBN = new BigNumber(nominalValue);
    const priceBN = new BigNumber(pricePerUnit);

    // Calculate total price: (nominalValue * pricePerUnit) / 100
    const totalPriceBN = nominalBN.multipliedBy(priceBN).dividedBy(100);

    // Calculate KFHC fees: totalPrice * 0.001
    const kfhcFeesBN = isFeeless ? new BigNumber(0) : totalPriceBN.multipliedBy(0.001);

    // Principal equals total price
    const principalBN = totalPriceBN;

    // Sukuk accrued profit is always computed on a 30/360 basis (see migrateBonds.js).
    // We auto-compute when we have enough info (coupon, dated date, and a
    // recognized/inferrable frequency); otherwise we leave it to the user to
    // enter. If the user has manually edited the value we preserve it.
    const { value: autoAccruedBN, canCompute } = computeSukukAccruedProfit(
        nominalValue,
        sukuk,
        settlementDate,
        tradeDate,
    );
    const accruedProfitBN = (canCompute && !preserveManualAccruedProfit)
        ? autoAccruedBN
        : new BigNumber(currentAccruedProfit || 0);

    // Settlement amount: principal + accruedProfit
    const settlementAmountBN = principalBN.plus(accruedProfitBN);

    // Calculate yield if sukuk coupon is available
    let calculatedYieldBN = new BigNumber(0);
    if (sukuk?.COUPON && pricePerUnit > 0) {
        calculatedYieldBN = new BigNumber(sukuk.COUPON).dividedBy(priceBN);
    }

    // Convert back to numbers for return
    return {
        totalPrice: totalPriceBN.toNumber(),
        kfhcFees: kfhcFeesBN.toNumber(),
        principal: principalBN.toNumber(),
        accruedProfit: accruedProfitBN.toNumber(),
        settlementAmount: settlementAmountBN.toNumber(),
        yield: calculatedYieldBN.toNumber(),
        custodianAccountId: custodianAccountId || '',
    };
};

const calculateMoneyMarketValues = (
    nominalValue: number,
    rate: number,
    days: number,
    currency: 'USD' | 'KWD'
): Partial<ManualTrade> => {
    const P = new BigNumber(nominalValue);
    const R = new BigNumber(rate).dividedBy(100);
    const D = new BigNumber(days);
    // Money market: USD accrues Act/360, KWD accrues Act/365.
    const BASIS = new BigNumber(currency === 'USD' ? 360 : 365);

    // profit = P * R * D / BASIS
    const accruedProfitBN = P.multipliedBy(R).multipliedBy(D).dividedBy(BASIS);
    const settlementAmountBN = P.plus(accruedProfitBN);

    return {
        accruedProfit: accruedProfitBN.toNumber(),
        settlementAmount: settlementAmountBN.toNumber(),
    };
};


const generateMoneyMarketDescription = (issuerCode: string, days: number, maturityDate: Timestamp | null): string => {
    if (!issuerCode || days <= 0 || !maturityDate) return '';
    const matDate = maturityDate.toDate();
    const matDateStr = `M${matDate.getDate().toString().padStart(2, '0')}/${(matDate.getMonth() + 1).toString().padStart(2, '0')}/${matDate.getFullYear()}`;
    return `${issuerCode.toUpperCase()} WAKALAH ${days} DAYS ${matDateStr}`;
};

const convertFirestoreToFormData = (trade: ManualTrade & { id?: string }): ManualTrade => {
    const formData = { ...createInitialFormData(new Date()), ...trade };
    if (trade.tradeDate instanceof Timestamp) formData.tradeDate = trade.tradeDate;
    if (trade.settlementDate instanceof Timestamp) formData.settlementDate = trade.settlementDate;
    if (trade.moneyMarket?.maturityDate instanceof Timestamp) {
        formData.moneyMarket = { ...trade.moneyMarket, maturityDate: trade.moneyMarket.maturityDate };
    }
    if (trade.sukuk?.MATURITYDATE instanceof Timestamp) {
        formData.sukuk = { ...trade.sukuk, MATURITYDATE: trade.sukuk.MATURITYDATE };
    }
    formData.portfolioId = trade.portfolioId || '';
    return formData;
};

// --- MAIN COMPONENT ---
export default function ManualTradeModal({ opened, onClose, onSubmit, tradeDate, existingTrade }: Props) {
    const db = useFirestore();
    const functions = useFunctions();
    const userContext = useContext(MyUserDataContext);
    const configContext = useContext(ConfigContext);
    const globalData = useContext(GlobalDataContext);
    const userClaims = userContext.firebaseTokenResult?.claims.customClaims as CustomClaims || {};
    const canSeeAllPortfolios = !!userClaims.admin || !!userClaims.oper;
    const allowedPortfolioTypeCodes = useMemo(
        () => getAllowedPortfolioTypeCodes(userContext, configContext),
        [userContext, configContext]
    );
    const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
    const [availableSukuks, setAvailableSukuks] = useState<Bond[]>([]);
    const [custodianAccounts, setCustodianAccounts] = useState<CustodianAccount[]>([]);
    const [showNewSukukForm, setShowNewSukukForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<ManualTrade>(createInitialFormData(tradeDate));
    const [newSukuk, setNewSukuk] = useState<Partial<Bond>>(INITIAL_NEW_SUKUK);
    const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
    // Tracks whether the user has manually edited the Sukuk accrued profit.
    // When true, calculateSukukValues preserves the user's value instead of
    // recomputing from nominal/coupon/days. Reset whenever the sukuk changes.
    const [accruedProfitManual, setAccruedProfitManual] = useState(false);
    // "Fetch from LSEG" state for the new-sukuk form. In LSEG-only mode
    // (config/tradesMap.newSukukLsegOnly) the LSEG fields are read-only and
    // "Create Sukuk" needs a successful fetch for the current ISIN.
    const [lsegLoading, setLsegLoading] = useState(false);
    const [lsegError, setLsegError] = useState('');
    const [lsegInfo, setLsegInfo] = useState<Omit<LsegSukukResult, 'sukuk'> | null>(null);
    const [lsegFetched, setLsegFetched] = useState(false);
    // Values the last LSEG fetch returned (or the saved sukuk when editing).
    const [lsegValues, setLsegValues] = useState<Partial<Bond> | null>(null);
    const lsegOnly = !!configContext?.tradesMap?.newSukukLsegOnly;
    // LSEG-only: everyone fetches. Otherwise manual entry as before, with Fetch for admins only.
    const canFetchLseg = lsegOnly || !!userClaims.admin;
    // LSEG-only: the API fields are disabled — before any fetch too, so they can't be typed
    // by hand. The one exception is a field the fetch left empty (e.g. an unmapped coupon
    // frequency), which opens after a successful fetch so "Create Sukuk" isn't stuck.
    const lsegLocked = (field: keyof Bond) => {
        if (!lsegOnly) return false;
        const fetchedEmpty = lsegFetched && lsegValues != null && (lsegValues[field] == null || lsegValues[field] === '');
        return !fetchedEmpty;
    };

    const isEditMode = !!existingTrade;
    const isLocked = existingTrade?.state === 'LOCKED';

    // Whether a fund portfolio supports the given asset class, based on the
    // fund's asset_class array in the funds collection (matched by portfolio id).
    const fundSupportsAssetClass = useCallback((portfolio: Portfolio, assetClass: string): boolean => {
        const fund = globalData.fundsMap?.[portfolio.id ?? ''];
        return !!fund?.asset_class?.some((ac) => `${ac}` === assetClass);
    }, [globalData.fundsMap]);

    // Money market currency for a fund portfolio, taken from the fund's currency
    // in the funds collection (only USD/KWD are supported; defaults to KWD).
    const fundCurrencyFor = useCallback((portfolioId?: string): 'USD' | 'KWD' => {
        const currency = globalData.fundsMap?.[portfolioId ?? '']?.currency;
        return currency === 'USD' ? 'USD' : 'KWD';
    }, [globalData.fundsMap]);

    // Instrument eligibility per portfolio. The asset_class check ONLY applies to
    // KFHC Fund portfolios; other portfolio types keep their previous behavior
    // (sukuk allowed, money market is funds-only).
    const portfolioAllowsSukuk = useCallback((portfolio: Portfolio): boolean => {
        if (portfolio.portfolioTypeCode === FUND_PORTFOLIO_TYPE_CODE) {
            return fundSupportsAssetClass(portfolio, ASSET_CLASS_SUKUK);
        }
        return true;
    }, [fundSupportsAssetClass]);

    const portfolioAllowsMoneyMarket = useCallback((portfolio: Portfolio): boolean => {
        if (portfolio.portfolioTypeCode === FUND_PORTFOLIO_TYPE_CODE) {
            return fundSupportsAssetClass(portfolio, ASSET_CLASS_MONEY_MARKET);
        }
        // Money market is only ever traded on fund portfolios.
        return false;
    }, [fundSupportsAssetClass]);

    // Show the Money Market tab only if the user has at least one accessible
    // portfolio whose fund supports money market.
    const canTradeMoneyMarket = useMemo(
        () => portfolios.some((p) => portfolioAllowsMoneyMarket(p)),
        [portfolios, portfolioAllowsMoneyMarket]
    );

    const resetNewSukuk = useCallback(() => {
        setNewSukuk(INITIAL_NEW_SUKUK);
        setLsegError('');
        setLsegInfo(null);
        setLsegFetched(false);
        setLsegValues(null);
    }, []);

    const fetchFromLseg = async () => {
        // Don't spend an LSEG/AI call on a sukuk that already exists in root securities.
        if (duplicateNewSukuk) {
            setLsegError(duplicateSukukError(duplicateNewSukuk));
            return;
        }
        setLsegLoading(true);
        setLsegError('');
        try {
            // Server runs up to 120s (LSEG + Geneva lists + AI); the SDK default is 70s.
            const { data } = await httpsCallable<{ isin: string }, LsegSukukResult>(functions, 'sukukFromLseg', { timeout: 130_000 })({ isin: newSukuk.ISIN || '' });
            const { sukuk, ...info } = data;
            const filled = {
                ...sukuk,
                ISSUEDATE: lsegDateToTs(sukuk.ISSUEDATE),
                MATURITYDATE: lsegDateToTs(sukuk.MATURITYDATE),
                FIRSTCOUPONDATE: lsegDateToTs(sukuk.FIRSTCOUPONDATE),
            } as Partial<Bond>;
            // Drop missing values: Firestore rejects undefined fields on save.
            const defined = Object.fromEntries(Object.entries(filled).filter(([, v]) => v !== undefined && v !== null)) as Partial<Bond>;
            // Keep the bond CODE already assigned when re-fetching on an existing trade.
            setNewSukuk(prev => ({ ...INITIAL_NEW_SUKUK, ...(prev.CODE && { CODE: prev.CODE }), ...defined }));
            setLsegValues(defined);
            setLsegInfo(info);
            setLsegFetched(true);
        } catch (e) {
            setLsegError(e instanceof Error ? e.message : String(e));
            setLsegFetched(false);
        } finally {
            setLsegLoading(false);
        }
    };

    const handleFieldBlur = (field: string) => setTouched(prev => ({ ...prev, [field]: true }));

    const resetForm = useCallback(() => {
        setFormData(createInitialFormData(tradeDate));
        setShowNewSukukForm(false);
        resetNewSukuk();
        setTouched({});
    }, [tradeDate, resetNewSukuk]);

    // Handle modal open/close state
    useEffect(() => {
        if (opened) {
            if (existingTrade) {
                const convertedData = convertFirestoreToFormData(existingTrade);
                setFormData(convertedData);
                if (convertedData.type === 'sukuk' && convertedData.transactionType === 'BUY' && convertedData.sukuk && !availableSukuks.some(s => s.CODE === convertedData.sukuk?.CODE)) {
                    setShowNewSukukForm(true);
                    setNewSukuk(convertedData.sukuk);
                    setLsegFetched(true); // already confirmed when the trade was created
                    setLsegValues(convertedData.sukuk);
                }
            } else {
                resetForm();
            }
        }
    }, [opened, existingTrade, resetForm, availableSukuks]);

    // Data Fetching
    useEffect(() => {
        if (!db || !tradeDate) return;
        const fetchData = async () => {
            const dateStr = `${tradeDate.getDate().toString().padStart(2, '0')}-${(tradeDate.getMonth() + 1).toString().padStart(2, '0')}-${tradeDate.getFullYear()}`;
            try {
                // Admin/oper see all portfolios; other users only see portfolios whose
                // effective type (portfolioContract ?? portfolioTypeCode) is granted via
                // their trade accounts in config/tradesMap. Firestore can't express the
                // fallback, so non-admins fetch and filter client-side.
                const canSeePortfolios = canSeeAllPortfolios || allowedPortfolioTypeCodes.length > 0;

                // Sukuks always come from the root securities collection: the per-date
                // snapshot lags behind and would let a user re-create an existing sukuk.
                const [portfoliosSnapshot, sukuksSnapshot, accountsSnapshot] = await Promise.all([
                    canSeePortfolios
                        ? getDocs(collection(db, `trade_dates/${dateStr}/portfolios`))
                        : Promise.resolve(null),
                    getDocs(query(collection(db, 'securities'), where('_INVESTMENTTYPE_CODE_', '==', 'SK'))),
                    getDocs(collection(db, 'custodian_accounts'))
                ]);

                const filterAllowed = (data: Portfolio[]) => canSeeAllPortfolios
                    ? data
                    : data.filter(p => allowedPortfolioTypeCodes.includes(getEffectivePortfolioType(p) ?? ''));

                let portfoliosData = portfoliosSnapshot
                    ? filterAllowed(portfoliosSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Portfolio[])
                    : [];
                if (canSeePortfolios && portfoliosData.length === 0) {
                    const mainPortfoliosSnapshot = await getDocs(collection(db, 'portfolios'));
                    portfoliosData = filterAllowed(mainPortfoliosSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Portfolio[]);
                }
                const sukuksData = sukuksSnapshot.docs.map(doc => doc.data()) as Bond[];

                setPortfolios(portfoliosData);
                setAvailableSukuks(sukuksData);
                setCustodianAccounts(accountsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as CustodianAccount[]);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        fetchData();
    }, [db, tradeDate, canSeeAllPortfolios, allowedPortfolioTypeCodes]);

    // --- COMPUTED VALUES ---
    const selectedPortfolio = useMemo(() => portfolios.find(p => p.id === formData.portfolioId), [formData.portfolioId, portfolios]);

    // Sukuk currency comes from the instrument's principal currency; default USD.
    // Set for BUY (new/existing) and SELL, since all paths populate formData.sukuk.
    const sukukCurrency: string = useMemo(
        () => formData.sukuk?._PRINCIPAL_CURRENCY_CODE_ || 'USD',
        [formData.sukuk]
    );

    const sukukBuyAvailableCash = useMemo(() => {
        if (!selectedPortfolio) return 0;
        // KWD is the local currency; every other currency is held as foreign cash.
        if (sukukCurrency === 'KWD') {
            const cashPosition = selectedPortfolio.position?.local_position?.find(p => p.code === 'KWD');
            return cashPosition?.total_market_value ?? 0;
        }
        const cashPosition = selectedPortfolio.position?.foreign_position?.find(p => p.code === sukukCurrency && p.cash === true);
        return cashPosition?.total_market_value ?? 0;
    }, [selectedPortfolio, sukukCurrency]);

    // Money market currency is taken from the selected fund's currency.
    const mmCurrency: 'USD' | 'KWD' = useMemo(() => {
        return fundCurrencyFor(selectedPortfolio?.id);
    }, [selectedPortfolio, fundCurrencyFor]);

    const mmBuyAvailableCash = useMemo(() => {
        if (!selectedPortfolio) return 0;
        if (mmCurrency === 'USD') {
            const cashPosition = selectedPortfolio.position?.foreign_position?.find(p => p.code === 'USD' && p.cash === true);
            return cashPosition?.total_market_value ?? 0;
        }
        const cashPosition = selectedPortfolio.position?.local_position?.find(p => p.code === 'KWD');
        return cashPosition?.total_market_value ?? 0;
    }, [selectedPortfolio, mmCurrency]);

    const portfolioSukuks = useMemo(() => {
        if (selectedPortfolio && formData.type === 'sukuk' && formData.transactionType === 'SELL') {
            const sukukCodes = availableSukuks.map(s => s.CODE);
            const foreignSukuk = selectedPortfolio.position?.foreign_position?.filter(p => p.code && sukukCodes.includes(p.code)) || [];
            const localSukuk = selectedPortfolio.position?.local_position?.filter(p => p.code && sukukCodes.includes(p.code)) || [];
            return [...foreignSukuk, ...localSukuk];
        }
        return [];
    }, [selectedPortfolio, formData.type, formData.transactionType, availableSukuks]);

    const selectedSellSukukHolding = useMemo(() => {
        if (formData.transactionType === 'SELL' && formData.selectedSukukId) {
            return portfolioSukuks.find(s => (s.firebase_key || s.code) === formData.selectedSukukId);
        }
        return undefined;
    }, [formData.transactionType, formData.selectedSukukId, portfolioSukuks]);

    const allocations = useMemo(() => {
        if (selectedPortfolio && formData.nominalValue > 0) {
            return [{ portfolioId: selectedPortfolio.id!, portfolioName: selectedPortfolio.name_en || selectedPortfolio.name || '', nominalValue: formData.nominalValue }];
        }
        return [];
    }, [selectedPortfolio, formData.nominalValue]);

    // Existing security with the same ISIN as the one being created, if any.
    const findSukukByIsin = useCallback((isin?: string): Bond | undefined => {
        const key = (isin || '').trim().toUpperCase();
        if (!key) return undefined;
        return availableSukuks.find(s => (s.ISIN || '').trim().toUpperCase() === key);
    }, [availableSukuks]);
    const duplicateNewSukuk = useMemo(() => findSukukByIsin(newSukuk.ISIN), [findSukukByIsin, newSukuk.ISIN]);
    const duplicateSukukError = (dup: Bond) => `Sukuk with ISIN ${dup.ISIN} already exists (${dup.CODE} - ${dup.DESCRIPTION}). Select it instead of creating a new one.`;

    // True when the new-sukuk form has both dates set and the maturity date
    // is not strictly after the issue date.
    const newSukukDatesInvalid = useMemo(() => {
        const issue = newSukuk.ISSUEDATE?.toDate();
        const maturity = newSukuk.MATURITYDATE?.toDate();
        if (!issue || !maturity) return false;
        return maturity.getTime() <= issue.getTime();
    }, [newSukuk.ISSUEDATE, newSukuk.MATURITYDATE]);

    // True when we have enough information (coupon, dated/issue date, and a
    // recognized or inferrable coupon frequency) to auto-calculate the Sukuk
    // accrued profit on a 30/360 basis. When false we leave the field empty
    // and let the user enter it manually.
    const canAutoComputeAccruedProfit = useMemo(() => {
        if (formData.type !== 'sukuk') return false;
        return computeSukukAccruedProfit(
            formData.nominalValue,
            formData.sukuk,
            formData.settlementDate,
            formData.tradeDate,
        ).canCompute;
    }, [formData.type, formData.nominalValue, formData.sukuk, formData.settlementDate, formData.tradeDate]);

    // --- FORM UPDATE HANDLERS ---
    const updateFormData = useCallback((updates: Partial<ManualTrade>, opts?: { userEditedAccruedProfit?: boolean }) => {
        if (isLocked) return;
        setFormData(prev => {
            const newData = { ...prev, ...updates };
            // If the sukuk or the selected sukuk changed, clear the manual-edit
            // flag so auto-computation can take over for the new instrument.
            const sukukChanged = newData.type === 'sukuk' && (
                prev.sukuk?.CODE !== newData.sukuk?.CODE
                || prev.sukuk?.ISIN !== newData.sukuk?.ISIN
                || prev.selectedSukukId !== newData.selectedSukukId
                || prev.type !== newData.type
            );
            if (sukukChanged) setAccruedProfitManual(false);
            if (opts?.userEditedAccruedProfit) setAccruedProfitManual(true);

            const preserveAccrued = opts?.userEditedAccruedProfit
                ? true
                : (sukukChanged ? false : accruedProfitManual);

            if (newData.type === 'sukuk') {
                const portfolioTypeCode = portfolios.find(p => p.id === newData.portfolioId)?.portfolioTypeCode || '';
                Object.assign(newData, calculateSukukValues(
                    newData.nominalValue,
                    newData.pricePerUnit,
                    newData.sukuk,
                    configContext?.tradesMap?.feelessPortfolios?.includes(newData.portfolioId),
                    configContext?.tradesMap?.sukukPortfolioTypeToCustodianAccountMap?.[portfolioTypeCode],
                    newData.settlementDate,
                    newData.tradeDate,
                    newData.accruedProfit,
                    preserveAccrued,
                ));
            } else if (newData.type === 'moneyMarket') {
                const mmCurrencyForCalc: 'USD' | 'KWD' = fundCurrencyFor(newData.portfolioId);
                Object.assign(newData, calculateMoneyMarketValues(
                    newData.nominalValue,
                    newData.moneyMarket?.rate || 0,
                    newData.moneyMarket?.days || 0,
                    mmCurrencyForCalc,
                ));
                newData.totalPrice = newData.nominalValue;
                newData.kfhcFees = 0;
                newData.principal = newData.nominalValue;
                newData.yield = 0;

                // Money Market is always annual at maturity: force frequency and
                // keep FirstCouponDate in sync with MaturityDate.
                const derivedCurrency = fundCurrencyFor(newData.portfolioId);
                const currentMm = (newData.moneyMarket as MoneyMarketInstrumentInForm | undefined);
                newData.moneyMarket = {
                    ...(currentMm ?? INITIAL_MONEY_MARKET),
                    frequency: 'Annual',
                    firstCouponDate: currentMm?.maturityDate ?? null,
                    currency: derivedCurrency,
                } as MoneyMarketInstrumentInForm;
            }

            return newData;
        });
    }, [isLocked, portfolios, configContext, accruedProfitManual, fundCurrencyFor]);

    const handleMoneyMarketChange = useCallback((field: keyof MoneyMarketInstrumentInForm, value: any) => {
        if (isLocked) return;
        updateFormData({
            moneyMarket: { ...(formData.moneyMarket as MoneyMarketInstrumentInForm), [field]: value }
        });
    }, [formData.moneyMarket, updateFormData, isLocked]);

    const handleInstrumentTypeChange = useCallback((value: string) => {
        if (isLocked) return;
        if (value !== 'sukuk' && value !== 'moneyMarket') return;
        const newInitialData = createInitialFormData(tradeDate);
        setAccruedProfitManual(false);
        updateFormData({ ...newInitialData, type: value as 'sukuk' | 'moneyMarket', portfolioId: '' });
        setShowNewSukukForm(false);
        resetNewSukuk();
        setTouched({});
    }, [updateFormData, isLocked, tradeDate, resetNewSukuk]);

    // Safety guard: if the user is not allowed to trade money market (non-fund user),
    // never let the form stay on the money market instrument type. Only applies once
    // portfolios have loaded and not while editing an existing (money market) trade,
    // to avoid switching away during the async load.
    useEffect(() => {
        if (!isEditMode && portfolios.length > 0 && !canTradeMoneyMarket && formData.type === 'moneyMarket') {
            handleInstrumentTypeChange('sukuk');
        }
    }, [isEditMode, portfolios.length, canTradeMoneyMarket, formData.type, handleInstrumentTypeChange]);

    // --- VALIDATION LOGIC ---
    const validation = useMemo(() => {
        const errors: { [key: string]: string } = {};
        if (!formData.portfolioId) errors.portfolio = 'Portfolio must be selected.';
        if (formData.nominalValue <= 0) errors.nominal = 'Value must be greater than 0.';

        if (formData.type === 'sukuk') {
            if (formData.transactionType === 'BUY') {
                // formData.sukuk is only set by selecting an existing sukuk or by the
                // "Create Sukuk" button; without it the doc would be saved with no
                // sukuk (and no CODE from the trigger).
                if (!formData.sukuk) {
                    errors.sukuk = showNewSukukForm
                        ? 'Click "Create Sukuk" to confirm the new Sukuk.'
                        : 'An existing Sukuk must be selected.';
                } else if (!formData.selectedSukukId) {
                    // New sukuk: refuse if a security with the same ISIN already exists.
                    const dup = findSukukByIsin(formData.sukuk.ISIN);
                    if (dup && dup.CODE !== formData.sukuk.CODE) errors.sukuk = duplicateSukukError(dup);
                }
                if (formData.settlementAmount > sukukBuyAvailableCash) {
                    errors.nominal = `Settlement amount exceeds available ${sukukCurrency} of ${sukukBuyAvailableCash.toLocaleString()}.`;
                }
            } else if (formData.transactionType === 'SELL') {
                if (!formData.selectedSukukId) errors.sukuk = 'A Sukuk from the portfolio must be selected.';
                if (selectedSellSukukHolding && formData.nominalValue > (selectedSellSukukHolding.quantity ?? 0)) {
                    errors.nominal = `Quantity cannot exceed available holdings of ${(selectedSellSukukHolding.quantity ?? 0).toLocaleString()}.`;
                }
            }
        } else if (formData.type === 'moneyMarket') {
            const mm = formData.moneyMarket;
            if (!mm || !mm.issuer || !mm.days || !mm.maturityDate || !formData.custodianAccountId) errors.moneyMarket = 'All money market fields are required.';
            if (formData.nominalValue > mmBuyAvailableCash) {
                errors.nominal = `Value cannot exceed available ${mmCurrency} of ${mmBuyAvailableCash.toLocaleString()}.`;
            }
        }
        return { errors, isValid: Object.keys(errors).length === 0 };
    }, [formData, showNewSukukForm, newSukuk, sukukBuyAvailableCash, sukukCurrency, mmBuyAvailableCash, mmCurrency, selectedSellSukukHolding, findSukukByIsin]);

    const isSubmitDisabled = isLocked || !validation.isValid;

    // --- RENDER FUNCTIONS ---
    const renderSukukForm = () => {
        const filteredSukuks = availableSukuks.filter(s => s.MATURITYDATE && formData.tradeDate && s.MATURITYDATE.toDate() > formData.tradeDate.toDate());
        return (
            <>
                <SegmentedControl value={formData.transactionType} color={formData.transactionType === 'BUY' ? 'green' : 'red'} onChange={(value) => {

                    // Keep the selected portfolio (it is valid for both Buy and Sell) so its
                    // positions/holdings stay visible; only reset the side-specific fields.
                    const updatedFormData = {
                        transactionType: value as 'BUY' | 'SELL',
                        nominalValue: 0,
                        sukuk: undefined,
                        selectedSukukId: '',
                        accruedProfit: 0,
                        pricePerUnit: 100,
                    };
                    updateFormData(updatedFormData);
                    setTouched({});
                }} data={[{ label: 'Buy', value: 'BUY' }, { label: 'Sell', value: 'SELL' }]} fullWidth my="md" disabled={isLocked} />
                {formData.transactionType === 'BUY' && (
                    <Paper p="md" withBorder>
                        <Stack gap="sm">
                            <Group justify="space-between">
                                <Text fw={500}>Sukuk Details</Text>
                                <Button size="xs" variant="light" onClick={() => setShowNewSukukForm(!showNewSukukForm)} disabled={isLocked}>{showNewSukukForm ? 'Select Existing' : 'Create New'}</Button>
                            </Group>
                            {!showNewSukukForm ? (
                                <Select
                                    label="Select Sukuk"
                                    placeholder="Choose from available sukuks"
                                    value={formData.sukuk?.CODE}
                                    onChange={(value) => {
                                        updateFormData({ sukuk: availableSukuks.find(s => s.CODE === value), selectedSukukId: value ?? '' }); handleFieldBlur('sukuk');
                                    }}
                                    onBlur={() => handleFieldBlur('sukuk')}
                                    data={filteredSukuks.map(s => ({ value: s.CODE || '', label: `${s.ISIN} - ${s.DESCRIPTION}` }))}
                                    searchable
                                    disabled={isLocked}
                                    error={touched.sukuk && validation.errors.sukuk} />
                            ) : (
                                <Stack gap="sm">
                                    <SimpleGrid cols={2}  >
                                        <TextInput
                                            label="ISIN"
                                            value={newSukuk.ISIN}
                                            // Editing the ISIN invalidates a previous LSEG fetch.
                                            onChange={(e) => { const ISIN = e.target.value; setNewSukuk(prev => ({ ...prev, ISIN })); setLsegFetched(false); }}
                                            required
                                            disabled={isLocked}
                                            error={duplicateNewSukuk ? duplicateSukukError(duplicateNewSukuk) : undefined}
                                            rightSectionWidth={canFetchLseg ? 60 : undefined}
                                            rightSection={canFetchLseg ? <Button size="compact-xs" variant="light" onClick={fetchFromLseg} loading={lsegLoading} disabled={isLocked || !newSukuk.ISIN?.trim() || !!duplicateNewSukuk}>Fetch</Button> : undefined}
                                        />
                                        <TextInput label="Description" value={newSukuk.DESCRIPTION} onChange={(e) => setNewSukuk(prev => ({ ...prev, DESCRIPTION: e.target.value }))} required disabled={isLocked || lsegLocked('DESCRIPTION')} />
                                        <TextInput label="Bloomberg ID" value={newSukuk.BLOOMBERGID} onChange={(e) => setNewSukuk(prev => ({ ...prev, BLOOMBERGID: e.target.value }))} required disabled={isLocked || lsegLocked('BLOOMBERGID')} />
                                        <NumberInput label="Shares Outstanding" value={newSukuk.SHARESOUTSTANDING} onChange={(value) => setNewSukuk(prev => ({ ...prev, SHARESOUTSTANDING: toNum(value) }))} decimalScale={0} required min={0} disabled={isLocked || lsegLocked('SHARESOUTSTANDING')} />
                                        <NumberInput label="Coupon Rate (%)" value={newSukuk.COUPON} onChange={(value) => setNewSukuk(prev => ({ ...prev, COUPON: toNum(value) }))} decimalScale={3} required min={0} disabled={isLocked || lsegLocked('COUPON')} />
                                        <DatePickerInput
                                            label="Issue Date"
                                            value={newSukuk.ISSUEDATE?.toDate()}
                                            onChange={(date) => setNewSukuk(prev => ({ ...prev, ISSUEDATE: date ? Timestamp.fromDate(fromMantineDate(date)!) : Timestamp.now() }))}
                                            required
                                            disabled={isLocked || lsegLocked('ISSUEDATE')}
                                        />
                                        <DatePickerInput
                                            label="Maturity Date"
                                            value={newSukuk.MATURITYDATE?.toDate()}
                                            onChange={(date) => setNewSukuk(prev => ({ ...prev, MATURITYDATE: date ? Timestamp.fromDate(fromMantineDate(date)!) : Timestamp.now() }))}
                                            required
                                            disabled={isLocked || lsegLocked('MATURITYDATE')}
                                            minDate={newSukuk.ISSUEDATE ? new Date(newSukuk.ISSUEDATE.toDate().getTime() + 24 * 60 * 60 * 1000) : undefined}
                                            error={newSukukDatesInvalid ? 'Maturity date must be after issue date' : undefined}
                                        />
                                        <DatePickerInput label="First Coupon Date" value={newSukuk.FIRSTCOUPONDATE?.toDate()} onChange={(date) => setNewSukuk(prev => ({ ...prev, FIRSTCOUPONDATE: date ? Timestamp.fromDate(fromMantineDate(date)!) : Timestamp.now() }))} required disabled={isLocked || lsegLocked('FIRSTCOUPONDATE')} />
                                        <GenevaApiSelect
                                            key="IssueCountry"
                                            linkTable="Country"
                                            label="Issue Country"
                                            value={newSukuk.ISSUE_COUNTRY_CODE || ''}
                                            onChange={(value) => setNewSukuk(prev => ({ ...prev, ISSUE_COUNTRY_CODE: value ?? '' }))}
                                            // onSelectItem={(item) => onLookupValueChange?.(field.xmlTag, item)}
                                            disabled={isLocked}
                                            required={true}
                                            placeholder={`Select Issue Country`}
                                            showApiLabel={true}
                                        />
                                        <GenevaApiSelect
                                            key="PriceDenomination"
                                            linkTable="MediumOfExchange"
                                            label="Price Denomination"
                                            value={newSukuk._PRINCIPAL_CURRENCY_CODE_ || ''}
                                            onChange={(value) => setNewSukuk(prev => ({ ...prev, _PRINCIPAL_CURRENCY_CODE_: value ?? '' }))}
                                            // onSelectItem={(item) => onLookupValueChange?.(field.xmlTag, item)}
                                            disabled={isLocked || lsegLocked('_PRINCIPAL_CURRENCY_CODE_')}
                                            required={true}
                                            placeholder={`Select Price Denomination`}
                                            showApiLabel={true}
                                        />
                                        <GenevaApiSelect
                                            key="SubIndustry"
                                            linkTable="SubIndustry "
                                            label="Sub Industry"
                                            value={newSukuk._UDF_SECTOR_ || ''}
                                            onChange={(value) => setNewSukuk(prev => ({ ...prev, _UDF_SECTOR_: value ?? '' }))}
                                            // onSelectItem={(item) => onLookupValueChange?.(field.xmlTag, item)}
                                            disabled={isLocked}
                                            required={true}
                                            placeholder={`Select Sub Industry`}
                                            showApiLabel={true}
                                        />
                                        {newSukuk.NEW_ISSUER ? (
                                            <TextInput
                                                label="New Issuer (will be created in Geneva)"
                                                value={newSukuk._ISSUER_NAMESORT_ || ''}
                                                onChange={(e) => { const name = e.target.value; setNewSukuk(prev => ({ ...prev, _ISSUER_NAMESORT_: name })); }}
                                                required
                                                disabled={isLocked}
                                                rightSectionWidth={110}
                                                rightSection={<Button size="compact-xs" variant="subtle" disabled={isLocked} onClick={() => setNewSukuk(prev => ({ ...prev, NEW_ISSUER: false, _ISSUER_NAMESORT_: '' }))}>Use existing</Button>}
                                            />
                                        ) : (
                                            <GenevaApiSelect
                                                key="Issuer"
                                                linkTable="Issuer "
                                                label="Issuer"
                                                value={newSukuk._ISSUER_NAMESORT_ || ''}
                                                onChange={(value) => setNewSukuk(prev => ({ ...prev, _ISSUER_NAMESORT_: value ?? '' }))}
                                                disabled={isLocked}
                                                required={true}
                                                placeholder={`Select Issuer`}
                                                showApiLabel={true}
                                            />
                                        )}
                                        <Select
                                            label="Coupon Frequency"
                                            placeholder="Select coupon frequency"
                                            value={newSukuk.COUPON_FREQUENCY || ''}
                                            onChange={(value) => setNewSukuk(prev => ({ ...prev, COUPON_FREQUENCY: value ?? '' }))}
                                            // onBlur={() => handleFieldBlur('sukuk')}
                                            data={FREQUENCY_OPTIONS.map(frequency => ({ value: frequency, label: frequency }))}
                                            searchable
                                            disabled={isLocked || lsegLocked('COUPON_FREQUENCY')}
                                            required
                                        />

                                    </SimpleGrid>
                                    {lsegError && <Alert color="red" variant="light">{lsegError}</Alert>}
                                    {lsegInfo && (
                                        <Alert color={lsegInfo.warning ? 'yellow' : 'teal'} variant="light">
                                            <Text size="sm">LSEG issuer: {lsegInfo.lsegIssuer || '—'}{lsegInfo.issuerConfidence != null && ` (AI match ${Math.round(lsegInfo.issuerConfidence * 100)}%)`}</Text>
                                            <Text size="sm">LSEG sub industry: {lsegInfo.lsegSubIndustry || '—'}{lsegInfo.subIndustryConfidence != null && ` (AI match ${Math.round(lsegInfo.subIndustryConfidence * 100)}%)`}</Text>
                                            {newSukuk.NEW_ISSUER && <Text size="sm">No matching Geneva issuer found; it will be created when the trade is sent to Geneva.</Text>}
                                            {lsegInfo.warning && <Text size="sm">{lsegInfo.warning}</Text>}
                                            <Text size="sm">Please select the Issue Country (country of risk) and review the Issuer and Sub Industry.</Text>
                                        </Alert>
                                    )}
                                    {lsegOnly && !lsegFetched && <Alert color="blue" variant="light">Enter the ISIN and click "Fetch" to fill the Sukuk details.</Alert>}
                                    <Alert color="blue" variant="light">
                                        If the required Issuer or Sub Industry value is not available in the
                                        dropdown, please contact the Operations team to have it added before
                                        creating the Sukuk.
                                    </Alert>
                                    <Button
                                        onClick={() => { updateFormData({ sukuk: newSukuk as Bond, selectedSukukId: '' }); setShowNewSukukForm(false); }}
                                        disabled={
                                            isLocked
                                            || (lsegOnly && !lsegFetched)
                                            || !newSukuk.ISIN
                                            || !newSukuk.DESCRIPTION
                                            || !newSukuk.COUPON
                                            || !newSukuk.ISSUEDATE
                                            || !newSukuk.MATURITYDATE
                                            || !newSukuk.ISSUE_COUNTRY_CODE
                                            || !newSukuk._PRINCIPAL_CURRENCY_CODE_
                                            || !newSukuk._UDF_SECTOR_
                                            || !newSukuk._ISSUER_NAMESORT_
                                            || newSukukDatesInvalid
                                            || !!duplicateNewSukuk
                                        }
                                    >
                                        Create Sukuk
                                    </Button>
                                </Stack>
                            )}
                            {(!showNewSukukForm && !formData.selectedSukukId && formData.sukuk) && <Card withBorder>
                                <Text fw={500}>New Sukuk Summary</Text>
                                <Text>ISIN: {formData.sukuk.ISIN}</Text>
                                <Text>Description: {formData.sukuk.DESCRIPTION}</Text>
                                <Text>Coupon Rate: {formData.sukuk.COUPON?.toFixed(3)}%</Text>
                                <Text>Maturity Date: {formData.sukuk.MATURITYDATE ? formData.sukuk.MATURITYDATE.toDate().toLocaleDateString() : 'Not Set'}</Text>
                            </Card>}
                        </Stack>
                    </Paper>
                )}
                {formData.transactionType === 'SELL' && (
                    <Select label="Select Sukuk to Sell" placeholder={portfolioSukuks.length === 0 ? "No Sukuks Available in Portfolio" : "Choose from portfolio holdings"} disabled={portfolioSukuks.length === 0 || isLocked} value={formData.selectedSukukId} onChange={(value) => { updateFormData({ selectedSukukId: value || '', sukuk: availableSukuks.find(s => s.CODE === value) }); handleFieldBlur('sukuk'); }} onBlur={() => handleFieldBlur('sukuk')} data={portfolioSukuks.map(s => ({ value: s.code || '', label: `${s.name} (Available: ${(s.quantity ?? 0).toLocaleString()})` }))} error={touched.sukuk && validation.errors.sukuk} required />
                )}
                <Divider label="Trading Details" my="md" />
                <Grid>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                        <NumberInput label={formData.transactionType === 'SELL' ? "Quantity" : "Nominal Value"} value={formData.nominalValue} onChange={(value) => updateFormData({ nominalValue: toNum(value) })} onBlur={() => handleFieldBlur('nominal')} required min={0} thousandSeparator disabled={isLocked} rightSection={formData.transactionType === 'SELL' && selectedSellSukukHolding ? (<Button size="xs" variant="light" onClick={() => updateFormData({ nominalValue: selectedSellSukukHolding.quantity || 0 })}>All</Button>) : null} rightSectionWidth={60} error={touched.nominal && validation.errors.nominal} />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 6 }}><NumberInput label="Price/Unit" value={formData.pricePerUnit} onChange={(value) => updateFormData({ pricePerUnit: toNum(value) })} decimalScale={4} required disabled={isLocked} /></Grid.Col>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                        <NumberInput
                            label="Accrued profit"
                            description={
                                canAutoComputeAccruedProfit
                                    ? (accruedProfitManual
                                        ? 'Manually edited (30/360 auto-calc disabled)'
                                        : 'Auto-calculated (30/360) — editable')
                                    : 'Coupon frequency unknown — please enter manually (30/360)'
                            }
                            value={formData.accruedProfit}
                            onChange={(value) => updateFormData(
                                { accruedProfit: toNum(value) },
                                { userEditedAccruedProfit: true },
                            )}
                            decimalScale={4}
                            required
                            disabled={isLocked}
                        />
                    </Grid.Col>
                </Grid>

                <Divider label="Account Details" my="md" />
                <Grid>
                    <Grid.Col span={{ base: 12, md: 12 }}><Select label="Custodian Account" placeholder="Select custodian account" value={formData.custodianAccountId} onChange={(value) => { updateFormData({ custodianAccountId: value || '' }); handleFieldBlur('custodian'); }} onBlur={() => handleFieldBlur('custodian')} data={custodianAccounts.filter(acc => acc.id && acc.NAMELINE1 === 'Euroclear').map(acc => ({ value: acc.id!, label: `${acc.NAMESORT} - ${acc.NAMELINE1}` }))} required mt="md" searchable disabled={true} error={touched.custodian && validation.errors.sukuk} /></Grid.Col>
                </Grid>
                <Divider label="Counterparty Details" my="md" />
                <Grid>
                    <Grid.Col span={{ base: 12, md: 6 }}><TextInput label="Name" value={formData.counterpartyName} onChange={(e) => updateFormData({ counterpartyName: e.currentTarget.value })} required disabled={isLocked} /></Grid.Col>
                    <Grid.Col span={{ base: 12, md: 6 }}><TextInput label="Account" value={formData.counterpartyAccount} onChange={(e) => updateFormData({ counterpartyAccount: e.currentTarget.value })} required disabled={isLocked} /></Grid.Col>
                </Grid>
            </>
        )
    };

    const renderMoneyMarketForm = () => (
        <>
            <Divider label="Instrument Details" my="md" />
            <Grid>
                <Grid.Col span={{ base: 12, md: 6 }}><TextInput label="Description" placeholder="e.g. KFH WAKALAH ..." value={formData.moneyMarket?.description} onChange={(e) => handleMoneyMarketChange('description', e.currentTarget.value)} required disabled={isLocked} /></Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}><GenevaApiSelect
                    key="Issuer"
                    linkTable="Issuer "
                    label="Issuer"
                    value={formData.moneyMarket?.issuer}
                    onChange={(value) => handleMoneyMarketChange('issuer', value)}
                    disabled={isLocked}
                    placeholder={`Select Issuer`}
                    showApiLabel={true}
                /></Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}><NumberInput label="Days" placeholder="e.g. 175" value={formData.moneyMarket?.days} onChange={(value) => handleMoneyMarketChange('days', toNum(value))} min={0} required disabled={isLocked} /></Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}><GenevaApiSelect
                    key="IssueCountry"
                    linkTable="Country"
                    label="Issue Country"
                    value={formData.moneyMarket?.issuerCountry || ''}
                    onChange={(value) => handleMoneyMarketChange('issuerCountry', value)}
                    disabled={isLocked}
                    placeholder={`Select Issue Country`}
                    showApiLabel={true}
                /></Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}><NumberInput label="Rate" placeholder="e.g. 4.125" value={formData.moneyMarket?.rate} onChange={(value) => handleMoneyMarketChange('rate', toNum(value))} min={0} required disabled={isLocked} /></Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                    <DatePickerInput label="Issue Date" placeholder="Select date" value={(formData.moneyMarket?.issueDate as Timestamp)?.toDate()} onChange={(date) => handleMoneyMarketChange('issueDate', date ? Timestamp.fromDate(fromMantineDate(date)!) : null)} required disabled={isLocked} />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                    <DatePickerInput
                        label="Maturity Date"
                        placeholder="Select date"
                        value={(formData.moneyMarket?.maturityDate as Timestamp)?.toDate()}
                        onChange={(date) => {
                            const ts = date ? Timestamp.fromDate(fromMantineDate(date)!) : null;
                            // Money Market pays annually at maturity: keep FirstCouponDate = MaturityDate
                            updateFormData({
                                moneyMarket: {
                                    ...(formData.moneyMarket as MoneyMarketInstrumentInForm),
                                    maturityDate: ts,
                                    firstCouponDate: ts,
                                    frequency: 'Annual',
                                },
                            });
                        }}
                        required
                        disabled={isLocked}
                    />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                    <TextInput
                        label="Price Denomination"
                        value={mmCurrency}
                        disabled
                    />
                </Grid.Col>
            </Grid>
            <Select label="Custodian Account" placeholder="Select custodian account" value={formData.custodianAccountId} onChange={(value) => { updateFormData({ custodianAccountId: value || '' }); handleFieldBlur('custodian'); }} onBlur={() => handleFieldBlur('custodian')} data={custodianAccounts.filter(acc => acc.id).map(acc => ({ value: acc.id!, label: `${acc.NAMESORT} - ${acc.NAMELINE1}` }))} required mt="md" searchable disabled={isLocked} error={touched.custodian && validation.errors.moneyMarket} />
            <Divider label="Transaction Details" my="md" />
            <NumberInput label="Nominal Value" value={formData.nominalValue} onChange={(value) => updateFormData({ nominalValue: toNum(value) })} onBlur={() => handleFieldBlur('nominal')} required min={0} thousandSeparator disabled={isLocked} error={touched.nominal && validation.errors.nominal} />
        </>
    );

    const renderSummary = () => (
        <Paper p="md" withBorder mt="md">
            <Stack gap="xs">
                {formData.type === 'sukuk' && (
                    <>
                        <Group justify="space-between"><Text size="sm">Total Price:</Text><Text fw={500}>{formData.totalPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text></Group>
                        <Group justify="space-between"><Text size="sm">KFHC Fees:</Text><Text fw={500}>{formData.kfhcFees.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</Text></Group>
                        <Divider />
                    </>
                )}
                <Group justify="space-between"><Text size="sm">Principal:</Text><Text fw={500}>{formData.principal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text></Group>
                <Group justify="space-between"><Text size="sm"> {formData.type === 'sukuk' ? 'Accrued Profit:' : 'Expected Profit:'}</Text><Text fw={500}>{formData.accruedProfit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text></Group>
                <Group justify="space-between"><Text fw={600} size="lg">{formData.type === 'sukuk' ? 'Settlement Amount:' : 'Maturity Amount:'}</Text><Text fw={600} size="lg">{formData.settlementAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text></Group>
                {formData.type === 'sukuk' && formData.sukuk && <Group justify="space-between"><Text size="sm">Yield:</Text><Text fw={500}>{(formData.yield * 100).toFixed(3)}%</Text></Group>}
            </Stack>
        </Paper>
    );

    // --- MAIN SUBMIT HANDLER ---
    const handleSubmit = async () => {
        if (isLocked || !validation.isValid) {
            setTouched({ portfolioId: true, sukuk: true, nominal: true, custodianAccountId: true });
            return;
        }
        setLoading(true);
        try {
            if (!db) throw new Error('Firestore connection not available');
            const dateStr = `${tradeDate.getDate().toString().padStart(2, '0')}-${(tradeDate.getMonth() + 1).toString().padStart(2, '0')}-${tradeDate.getFullYear()}`;
            const manualTradesPath = `trade_dates/${dateStr}/manual_trades`;

            let tradeData: Partial<ManualTrade> & { [key: string]: any } = {
                ...formData,
                ...(isEditMode ? {
                    updatedAt: Timestamp.now(),
                    updatedBy: userContext?.user?.displayName || '',
                    updatedById: userContext?.user?.uid || '',
                } : {
                    createdAt: Timestamp.now(),
                    createdBy: userContext?.user?.displayName || '',
                    createdById: userContext?.user?.uid || '',
                }),
            };

            // `id` comes from existingTrade and must not be persisted into the doc.
            delete tradeData.id;
            // The state is owned by the Geneva flow (trigger); an edit must not reset it.
            if (isEditMode) delete tradeData.state;
            // Strip the other instrument by type (not by presence): the form always
            // seeds moneyMarket defaults, and on edit updateDoc merges, so a stale
            // block must be removed with deleteField().
            const otherInstrumentKeys = tradeData.type === 'sukuk'
                ? ['moneyMarket']
                : ['sukuk', 'selectedSukukId'];
            for (const key of otherInstrumentKeys) {
                if (isEditMode) tradeData[key] = deleteField();
                else delete tradeData[key];
            }
            if (tradeData.type === 'moneyMarket' && tradeData.moneyMarket) {
                tradeData.moneyMarket = { ...tradeData.moneyMarket, maturityDate: tradeData.moneyMarket.maturityDate ? tradeData.moneyMarket.maturityDate : null };
            }
            // Denormalize the portfolio type onto the trade so Firestore queries/rules
            // can enforce visibility per portfolio type. Effective type: the manual-trades
            // rules match against the portfolioTypeCodes claim, which includes
            // portfolioContract values for accounts that use them.
            if (selectedPortfolio && getEffectivePortfolioType(selectedPortfolio)) {
                tradeData.portfolioTypeCode = getEffectivePortfolioType(selectedPortfolio);
            }
            if (selectedPortfolio?.portfolioTypeCode == 'KFHC Fund') {
                tradeData.fundStructure = selectedPortfolio.id;
            }

            if (isEditMode && existingTrade?.id) {
                await updateDoc(doc(db, manualTradesPath, existingTrade.id), tradeData);
            } else {
                await addDoc(collection(db, manualTradesPath), tradeData);
            }
            onSubmit?.(formData, isEditMode);
            onClose();
        } catch (error) {
            console.error(`Error ${isEditMode ? 'updating' : 'submitting'} trade:`, error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    return (
        <Modal opened={opened} onClose={handleClose} title={<Group gap="xs"><Text>{isEditMode ? 'Edit Trade' : 'Add New Trade'}</Text>{isLocked && <IconLock size={20} />}</Group>} size="xl" scrollAreaComponent={ScrollArea.Autosize}>
            <Stack gap="md">
                {isLocked && <Alert color="red" icon={<IconLock />} variant="light">This trade is locked and cannot be edited.</Alert>}
                {canTradeMoneyMarket && (
                    <SegmentedControl value={formData.type} onChange={handleInstrumentTypeChange} data={[{ label: 'Sukuk', value: 'sukuk' }, { label: 'Money Market', value: 'moneyMarket' }]} color="blue" fullWidth disabled={isLocked} />
                )}

                <Box>
                    <Select
                        // Remount when the instrument type changes (which resets portfolioId)
                        // so the searchable input never keeps a stale label out of sync with value.
                        key={`portfolio-${formData.type}`}
                        label="Portfolio"
                        placeholder="Select portfolio"
                        value={formData.portfolioId || null}
                        onChange={(value) => { updateFormData({ portfolioId: value || '' }); handleFieldBlur('portfolioId'); }}
                        onBlur={() => handleFieldBlur('portfolioId')}
                        data={portfolios.filter(p => {
                            if (!p.id) return false;
                            // For KFHC Fund portfolios, instrument eligibility is driven by the
                            // fund's asset_class; other portfolio types keep prior behavior.
                            if (formData.type === 'moneyMarket') return portfolioAllowsMoneyMarket(p);
                            return portfolioAllowsSukuk(p);
                        }).map(p => ({ value: p.id!, label: `${p.id} - ${p.name_en || p.name || ''}` }))}
                        required
                        searchable
                        disabled={isLocked}
                        error={touched.portfolioId && validation.errors.portfolio}
                    />
                    {formData.portfolioId && (
                        <Text size="sm" c="dimmed" mt={4}>
                            {formData.type === 'sukuk' && `Available Cash (${sukukCurrency}): ${sukukBuyAvailableCash.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            {formData.type === 'moneyMarket' && `Available Cash (${mmCurrency}): ${mmBuyAvailableCash.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        </Text>
                    )}
                </Box>

                {formData.type === 'sukuk' ? renderSukukForm() : renderMoneyMarketForm()}

                <Grid mt="md">
                    <Grid.Col span={{ base: 12, md: 6 }}>
                        <DatePickerInput label="Trade Date" value={formData.tradeDate?.toDate()} required disabled />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                        <DatePickerInput label="Settlement Date" value={formData.settlementDate?.toDate()} onChange={(date) => updateFormData({ settlementDate: date ? Timestamp.fromDate(fromMantineDate(date)!) : Timestamp.now() })} required disabled minDate={formData.tradeDate?.toDate()} />
                    </Grid.Col>
                </Grid>

                {renderSummary()}

                {allocations.length > 0 && (
                    <>
                        <Divider label="Allocation Details" my="md" />
                        <Paper p="md" withBorder>
                            <Stack gap="xs">
                                <Text fw={500}>Portfolio Allocation</Text>
                                <Group justify="space-between"><Text size="sm">Portfolio ID:</Text><Text size="sm">{allocations[0].portfolioId}</Text></Group>
                                <Group justify="space-between"><Text size="sm">Portfolio Name:</Text><Text size="sm">{allocations[0].portfolioName}</Text></Group>
                                <Group justify="space-between"><Text size="sm">{formData.transactionType === 'SELL' ? "Quantity:" : "Nominal Value:"}</Text><Text size="sm" fw={500}>{allocations[0].nominalValue.toLocaleString()}</Text></Group>
                            </Stack>
                        </Paper>
                    </>
                )}

                <Group justify="flex-end" mt="xl">
                    <Button variant="default" onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSubmit} loading={loading} disabled={isSubmitDisabled}>{isEditMode ? 'Update Trade' : 'Add Trade'}</Button>
                </Group>
            </Stack>
        </Modal>
    );
}
